import { useEffect, useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { apiClient } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'

// Admin-only cross-user view of background keyword-monitoring streams
// (apps/api/services/stream_worker.py's hourly sweep target) — separate from
// the owner-scoped Social Stream "Keyword Monitor" panel every user sees for
// their own streams. Lets an admin see how many are running, who created
// each, and pause/resume any of them.
type AdminStream = {
  id: string
  user_id: string
  user_email: string | null
  user_name: string | null
  keyword: string
  platforms: string[]
  is_active: boolean
  created_at: string | null
  last_fetch_at: string | null
  fetch_interval_minutes: number
  total_posts: number
}

export default function StreamsSection() {
  const { toast } = useToast()
  const [streams, setStreams] = useState<AdminStream[]>([])
  const [total, setTotal] = useState(0)
  const [activeCount, setActiveCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [togglingId, setTogglingId] = useState<string | null>(null)

  const load = async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await apiClient.getAdminStreams(true)
      setStreams((res as any).streams || [])
      setTotal((res as any).total || 0)
      setActiveCount((res as any).active_count || 0)
    } catch (e: any) {
      console.error('Failed to load streams', e)
      setError(e.message || 'Failed to load streams')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const toggleStream = async (stream: AdminStream) => {
    const nextActive = !stream.is_active
    setTogglingId(stream.id)
    // Optimistic update — reverted on failure.
    setStreams((prev) => prev.map((s) => (s.id === stream.id ? { ...s, is_active: nextActive } : s)))
    try {
      if (nextActive) {
        await apiClient.adminActivateStream(stream.id)
      } else {
        await apiClient.adminDeactivateStream(stream.id)
      }
      setActiveCount((prev) => prev + (nextActive ? 1 : -1))
      toast({ title: `${nextActive ? 'Activated' : 'Deactivated'} "${stream.keyword}"` })
    } catch (e: any) {
      console.error('Failed to toggle stream', e)
      setStreams((prev) => prev.map((s) => (s.id === stream.id ? { ...s, is_active: stream.is_active } : s)))
      toast({ title: 'Failed to change stream status', description: e.message, variant: 'destructive' })
    } finally {
      setTogglingId(null)
    }
  }

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return streams
    return streams.filter(
      (s) =>
        s.keyword?.toLowerCase().includes(q) ||
        s.user_email?.toLowerCase().includes(q) ||
        s.user_name?.toLowerCase().includes(q)
    )
  }, [streams, filter])

  return (
    <Card>
      <CardHeader>
        <CardTitle>Monitoring Streams</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Every background keyword-monitoring stream across every user — who created it, what it's
          watching, and whether it's currently running.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="rounded border px-3 py-2">
            <div className="text-2xl font-semibold leading-none">{total}</div>
            <div className="text-xs text-muted-foreground mt-1">Total streams</div>
          </div>
          <div className="rounded border px-3 py-2">
            <div className="text-2xl font-semibold leading-none text-emerald-500">{activeCount}</div>
            <div className="text-xs text-muted-foreground mt-1">Currently active</div>
          </div>
          <Input
            placeholder="Filter by keyword or user…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="max-w-xs ml-auto"
          />
        </div>

        {error && <p className="text-xs text-destructive">{error}</p>}

        <div className="border rounded max-h-[32rem] overflow-y-auto">
          {loading ? (
            <p className="text-sm text-muted-foreground p-4">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground p-4">No streams match.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-background">
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-2 px-2">User</th>
                  <th className="py-2 px-2">Keyword</th>
                  <th className="py-2 px-2">Platforms</th>
                  <th className="py-2 px-2">Interval</th>
                  <th className="py-2 px-2">Last Fetch</th>
                  <th className="py-2 px-2">Created</th>
                  <th className="py-2 px-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id} className="border-b border-border/40">
                    <td className="py-2 px-2">{s.user_email || s.user_id}</td>
                    <td className="py-2 px-2 font-mono">{s.keyword}</td>
                    <td className="py-2 px-2">{(s.platforms || []).join(', ')}</td>
                    <td className="py-2 px-2 text-muted-foreground">{s.fetch_interval_minutes}min</td>
                    <td className="py-2 px-2 text-muted-foreground">
                      {s.last_fetch_at ? new Date(s.last_fetch_at).toLocaleString() : 'Never'}
                    </td>
                    <td className="py-2 px-2 text-muted-foreground">
                      {s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-2 px-2">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={s.is_active}
                          disabled={togglingId === s.id}
                          onCheckedChange={() => toggleStream(s)}
                        />
                        <span className={s.is_active ? 'text-emerald-500' : 'text-muted-foreground'}>
                          {s.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
